// Website Development service page - Premium redesign
import React from 'react';
import { Navigation } from '@/components/navigation';
import { Footer } from '@/components/footer';
import { ServiceDetailPage } from '@/components/service-detail-page';
import { SEO } from '@/components/seo';

const websiteDevelopmentService = {
  name: "Website Development",
  promise: "Fast, beautiful websites that turn visitors into customers",
  primaryKPI: "+47% average conversion rate increase",
  signals: {
    bestFor: "Growing businesses ready to establish or upgrade their digital presence",
    timeline: "2-4 weeks from kickoff to launch",
    team: "Senior designer + developer + SEO specialist",
    deliverables: "Custom website + performance optimization + analytics"
  },
  outputs: [
    "Mobile-first responsive design with modern aesthetics",
    "Core Web Vitals optimization (90+ Lighthouse scores)",
    "Conversion tracking and analytics setup",
    "Content management system (WordPress/Webflow)",
    "SEO foundation (meta tags, schema, sitemap)",
    "Performance optimization and CDN setup",
    "2 weeks post-launch support and fixes"
  ],
  process: [
    {
      step: "Discovery & Strategy",
      description: "Understand your brand, goals, and target audience",
      ownership: "We lead discovery, you provide business context",
      artifacts: ["Site map", "User flow diagrams", "Technical requirements"]
    },
    {
      step: "Design & Prototype",
      description: "Create pixel-perfect designs focused on conversion",
      ownership: "We design, you approve key pages",
      artifacts: ["High-fidelity mockups", "Interactive prototype", "Style guide"]
    },
    {
      step: "Development & Testing",
      description: "Build with performance and SEO best practices",
      ownership: "We code and test, you review staging environment",
      artifacts: ["Staging website", "Performance audit", "Cross-browser QA report"]
    },
    {
      step: "Launch & Optimize",
      description: "Deploy and monitor initial performance metrics",
      ownership: "We launch and monitor, you provide feedback",
      artifacts: ["Live website", "Analytics dashboard", "Post-launch optimization report"]
    }
  ],
  proof: [
    {
      client: "SaaS startup (Series A)",
      challenge: "High traffic but low demo request conversions",
      result: "Redesigned homepage with clear value prop and streamlined CTA flow",
      metric: "+63% demo signups in first 30 days"
    },
    {
      client: "Professional services firm",
      challenge: "Outdated website with 4.2s load time hurting credibility",
      result: "Modern redesign with 1.8s load time and trust-building elements",
      metric: "+28% contact form submissions"
    },
    {
      client: "E-commerce retailer",
      challenge: "High bounce rate on product pages (68%)",
      result: "Improved product page UX with better imagery and checkout flow",
      metric: "Reduced bounce rate to 34% and +41% sales"
    }
  ],
  faqs: [
    {
      question: "What's included in the base price?",
      answer: "Complete website with up to 10 pages, mobile optimization, CMS setup, basic SEO, and analytics integration. Additional pages are $200 each. Complex features like booking systems or member areas are quoted separately."
    },
    {
      question: "How long does it take?",
      answer: "Most sites launch within 2-4 weeks depending on content readiness and feedback speed. Complex features or custom integrations may add 1-2 weeks. We provide a detailed timeline after discovery."
    },
    {
      question: "Do you provide hosting and maintenance?",
      answer: "We set up hosting on your preferred platform (Webflow, WordPress, or custom). Hosting costs are separate ($15-50/month typically). We offer ongoing maintenance packages starting at $200/month for updates, security, and backups."
    },
    {
      question: "What if I need changes after launch?",
      answer: "First 2 weeks of minor updates are included. After that, changes are billed at $150/hour with a 30-minute minimum. We also offer monthly retainer packages for ongoing updates and optimization."
    },
    {
      question: "How do you ensure good performance and SEO?",
      answer: "We optimize images, minify code, use CDN, implement lazy loading, and target 90+ Core Web Vitals scores. For SEO, we implement proper meta tags, schema markup, sitemaps, and mobile optimization. Performance and SEO reports are included."
    },
    {
      question: "Can you work with our existing brand?",
      answer: "Absolutely. We'll work within your brand guidelines (colors, fonts, tone). If you need brand development or a refresh, that's a separate project we can also handle."
    }
  ]
};

const WebsiteDevelopment = () => {
  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="Website Development Services - Custom Sites That Convert | AffinityAim"
        description="Professional website development with 47% avg conversion boost. Mobile-first design, 90+ Lighthouse scores, 2-4 week delivery. Get your custom quote today."
        canonical="https://www.affinityaim.com/services/website-development"
        keywords="website development services, custom website design, responsive web development"
        type="service"
      />
      <Navigation />
      <main className="pt-20">
        <ServiceDetailPage service={websiteDevelopmentService} />
      </main>
      <Footer />
    </div>
  );
};

export default WebsiteDevelopment;